import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/react";
import { api } from "@/lib/api";
import type { GenerateRequest, GenerateResponse, GenerationStatusResponse } from "@/types";

export function useGenerate() {
  const { user } = useUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: Omit<GenerateRequest, "user_id">) => {
      if (!user?.id) throw new Error("No user ID");
      return api.post<GenerateResponse>("/generate", {
        ...params,
        user_id: user.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

export function useGenerationStatus(generationId: string | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["generation", generationId],
    queryFn: async () => {
      if (!generationId) throw new Error("No generation ID");
      return api.get<GenerationStatusResponse>(
        `/generation/${generationId}/status`,
      );
    },
    enabled: !!generationId,
    refetchInterval: (query) => {
      const status = query.state.data?.generation?.status;
      if (status === "completed" || status === "failed") {
        queryClient.invalidateQueries({ queryKey: ["videos"] });
        queryClient.invalidateQueries({ queryKey: ["home-recent-videos"] });
        queryClient.invalidateQueries({ queryKey: ["user"] });
        return false;
      }
      return 3000;
    },
    refetchIntervalInBackground: true,
    retry: false,
  });
}
